/* Phaya Nak TCG — small, dependency-free behaviours for the pn-* sections. */
(() => {
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Mobile nav drawer ---------- */
  function initDrawer() {
    const drawer = document.querySelector('[data-pn-drawer]');
    const opener = document.querySelector('[data-pn-drawer-open]');
    if (!drawer || !opener) return;

    const open = () => {
      drawer.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      drawer.querySelector('[data-pn-drawer-close]:not(.pn-drawer__scrim)')?.focus();
    };
    const close = () => {
      drawer.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      opener.focus();
    };
    opener.addEventListener('click', open);
    $$('[data-pn-drawer-close]', drawer).forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });
  }

  /* ---------- Header search: game dropdown becomes a tag filter ---------- */
  function initHeaderSearch() {
    $$('[data-pn-search]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        $$('input[data-pn-game-filter]', form).forEach((el) => el.remove());
        const select = form.querySelector('[data-pn-search-game]');
        const q = form.querySelector('input[name="q"]');
        if (q && !q.value.trim()) {
          // No text: browse the whole game instead of an empty search
          e.preventDefault();
          if (select && select.value) {
            const tag = (select.dataset.tagPrefix || 'Game: ') + select.value;
            window.location.href = `/collections/all?${new URLSearchParams({ 'filter.p.tag': tag })}`;
          } else {
            q.focus();
          }
          return;
        }
        if (select && select.value) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'filter.p.tag';
          hidden.value = (select.dataset.tagPrefix || 'Game: ') + select.value;
          hidden.dataset.pnGameFilter = '';
          form.appendChild(hidden);
        }
      });
    });
  }

  /* ---------- Listing filter bar ---------- */
  function initFilters() {
    $$('[data-pn-filters]').forEach((form) => {
      const panels = $$('[data-pn-filter]', form);

      // Only one dropdown open at a time
      panels.forEach((d) =>
        d.addEventListener('toggle', () => {
          if (d.open) panels.forEach((o) => o !== d && (o.open = false));
        })
      );

      // Close on outside click / Escape
      document.addEventListener('click', (e) => {
        if (!form.contains(e.target)) panels.forEach((d) => (d.open = false));
      });
      form.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openPanel = panels.find((d) => d.open);
        if (openPanel) {
          openPanel.open = false;
          openPanel.querySelector('summary')?.focus();
        }
      });

      // Keep URLs clean: drop empty price inputs
      form.addEventListener('submit', () => {
        $$('input[type="number"]', form).forEach((el) => {
          if (el.value === '') el.disabled = true;
        });
      });
    });

    // Sort select lives outside the form (form= attribute); submit on change
    $$('[data-pn-sort]').forEach((select) =>
      select.addEventListener('change', () => {
        const form = select.form;
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      })
    );
  }

  /* ---------- Card "Add to cart" without leaving the page ---------- */
  function initCardAdd() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest?.('[data-pn-add]');
      if (!form) return;
      const actions = window.Shopify && window.Shopify.actions;
      if (!actions || typeof actions.updateCart !== 'function') return; // plain POST fallback

      e.preventDefault();
      const button = form.querySelector('button');
      const label = button.textContent;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      try {
        const result = await actions.updateCart({
          lines: [{ merchandiseId: form.elements.id.value, quantity: Number(form.elements.quantity.value) || 1 }],
        });
        if (result && result.userErrors && result.userErrors.length) {
          button.textContent = result.userErrors[0].message || 'Could not add';
        } else {
          button.textContent = 'Added';
          button.classList.add('pn-card__bar--done');
          if (typeof actions.openCart === 'function') actions.openCart();
        }
      } catch (err) {
        form.submit(); // let the server handle it
        return;
      } finally {
        button.removeAttribute('aria-busy');
      }
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
        button.classList.remove('pn-card__bar--done');
      }, 2200);
    });
  }

  /* ---------- Product tabs ---------- */
  function initTabs() {
    $$('[data-pn-tabs]').forEach((root) => {
      const tabs = $$('[role="tab"]', root);
      if (!tabs.length) return;
      const select = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
      };
      // Deep link: /collections#one-piece opens that tab
      const fromHash = tabs.find((t) => t.dataset.pnHash && `#${t.dataset.pnHash}` === window.location.hash);
      if (fromHash) select(fromHash);
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          select(tab);
          if (tab.dataset.pnHash) history.replaceState(null, '', `#${tab.dataset.pnHash}`);
        });
        tab.addEventListener('keydown', (e) => {
          const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;
          const next = tabs[(i + step + tabs.length) % tabs.length];
          select(next);
          next.focus();
        });
      });
    });
  }

  /* ---------- Advanced search: name → /search, no name → browse collection ---------- */
  function initAdvanced() {
    $$('[data-pn-advanced]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const data = new FormData(form);
        const q = (data.get('q') || '').toString().trim();
        const params = new URLSearchParams();

        for (const [key, raw] of data.entries()) {
          const value = raw.toString().trim();
          if (!value) continue;
          if (!q && (key === 'q' || key === 'type' || key === 'options[prefix]')) continue;
          if (key === 'q') {
            params.set('q', q);
            continue;
          }
          params.append(key, value);
        }

        // Search pages only support relevance and price sorting
        const sort = params.get('sort_by');
        const browseOnly = form.querySelector(`option[value="${sort}"][data-browse-only]`);
        if (q && browseOnly) params.delete('sort_by');

        const base = q ? form.getAttribute('action') : form.dataset.browseUrl || '/collections/all';
        window.location.href = `${base}?${params.toString()}`;
      });
    });
  }

  /* ---------- Hero card carousel: one card at a time, native swipe ---------- */
  function initShowcase() {
    $$('[data-pn-showcase]').forEach((root) => {
      if (root.dataset.pnReady) return;
      root.dataset.pnReady = '1';
      const track = root.querySelector('[data-pn-showcase-track]');
      const slides = track ? $$('.pn-showcase__slide', track) : [];
      const n = slides.length;
      if (n < 2) return;
      const count = root.querySelector('[data-pn-showcase-count]');
      const bar = root.querySelector('[data-pn-showcase-bar]');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = Math.max(2, Number(root.dataset.interval) || 4) * 1000;
      let current = 0;
      let timer = null;
      let visible = false;
      let paused = false;
      let target = null; // card a button press is scrolling to

      const offsetFor = (slide) => slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;

      const mark = (index) => {
        current = index;
        slides.forEach((s, i) => s.classList.toggle('is-current', i === index));
        if (count) count.textContent = `${index + 1} / ${n}`;
      };

      const go = (index) => {
        const i = (index + n) % n;
        target = i;
        track.scrollTo({ left: offsetFor(slides[i]), behavior: reduce ? 'auto' : 'smooth' });
        mark(i);
      };

      // Work out which card is centred after a swipe
      let raf = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const mid = track.scrollLeft + track.clientWidth / 2;
            let best = 0;
            let bestDist = Infinity;
            slides.forEach((s, i) => {
              const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
              if (d < bestDist) {
                bestDist = d;
                best = i;
              }
            });
            // While a button scroll is in flight, keep the counter on its destination
            if (target !== null) {
              if (best !== target) return;
              target = null;
            }
            if (best !== current) mark(best);
          });
        },
        { passive: true }
      );

      const stop = () => {
        clearInterval(timer);
        timer = null;
      };
      const start = () => {
        if (reduce || timer || paused || !visible || document.hidden) return;
        timer = setInterval(() => go(base() + 1), delay);
      };
      const pause = () => {
        paused = true;
        stop();
      };
      const resume = () => {
        paused = false;
        start();
      };

      // Tapping a side card brings it to the middle instead of leaving the page
      slides.forEach((slide, i) =>
        slide.addEventListener('click', (e) => {
          if (i !== current) {
            e.preventDefault();
            go(i);
          }
        })
      );
      // Rapid presses stack: each one steps from where the last press was heading
      const base = () => (target !== null ? target : current);
      root.querySelector('[data-pn-showcase-prev]')?.addEventListener('click', () => go(base() - 1));
      root.querySelector('[data-pn-showcase-next]')?.addEventListener('click', () => go(base() + 1));

      // Any hand on the carousel stops the autoplay for good on touch, until the mouse leaves on desktop
      track.addEventListener('pointerdown', (e) => {
        target = null; // a finger on the cards takes over from any button scroll
        if (e.pointerType !== 'mouse') pause();
      });
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', (e) => {
        if (!root.contains(e.relatedTarget)) resume();
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          visible ? start() : stop();
        }).observe(root);
      } else {
        visible = true;
        start();
      }
      window.addEventListener('resize', () => track.scrollTo({ left: offsetFor(slides[current]) }));

      if (bar) bar.hidden = false;
      mark(0);
    });
  }

  /* ---------- Mobile header: search opens under the bar ---------- */
  function initSearchToggle() {
    $$('[data-pn-search-toggle]').forEach((btn) => {
      const header = btn.closest('[data-pn-header]');
      const form = header?.querySelector('[data-pn-search]');
      if (!form) return;
      btn.addEventListener('click', () => {
        const open = !header.classList.contains('is-searching');
        header.classList.toggle('is-searching', open);
        btn.setAttribute('aria-expanded', String(open));
        if (open) form.querySelector('input[name="q"]')?.focus();
      });
      form.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && header.classList.contains('is-searching')) {
          header.classList.remove('is-searching');
          btn.setAttribute('aria-expanded', 'false');
          btn.focus();
        }
      });
    });
  }

  /* Horizon's product page uses --header-height for sticky media */
  function setHeaderHeight() {
    const header = document.querySelector('[data-pn-header]');
    if (!header) return;
    const sticky = header.classList.contains('pn-header--sticky');
    document.body.style.setProperty('--header-height', `${sticky ? header.offsetHeight : 0}px`);
  }

  function init() {
    setHeaderHeight();
    initShowcase();
    initSearchToggle();
    initDrawer();
    initHeaderSearch();
    initFilters();
    initCardAdd();
    initTabs();
    initAdvanced();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-bind tabs when sections are edited in the theme editor
  document.addEventListener('shopify:section:load', () => {
    initShowcase();
    initTabs();
    initFilters();
  });
})();
